import React from 'react';
import clsx from 'clsx';
import {
    Box, Checkbox, DialogContent, DialogTitle,
    FormControlLabel, Typography,
} from '@material-ui/core';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';
import ClusterStorageConfig from 'app/components/storage/ClusterStorageConfig';

import * as formHelpers from './formHelpers';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import BasicInformationSection from '../components/BasicInformationSection';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration';
import ImageSelectionSection from '../components/ImageSelectionSection';
import StorageSettingsSection from '../components/StorageSettingsSection';
import NetworkSettingsSection from '../components/NetworkSettingsSection';
import DescriptionSection from '../components/DescriptionSection';
import CredentialSelector from '../components/CredentialSelector';
import RegionSelector from '../components/RegionSelector';
import ZoneSelector from '../components/ZoneSelector';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';
import ComputeNodesSection from './ComputeNodesSection';
import HeadNodePDSSection from './HeadNodePDSSection';

// IBM VPC boot-disk limits, and the single source of truth for both the Storage Settings
// fields and the save-blocking validation below. The generic provider volsize range does
// not apply here: IBM caps a boot volume at 250 GB (bigger data belongs on an attached
// volume - see Head Node Performance Data Storage), and the real floor is whatever the
// selected image reports, which is layered on via imageConstraint.
const IBM_VOL_LIMITS = { min: 100, max: 250 };

const imageConstraintOf = (f) => ({
    minGb: (f && f.imageMinSizeGb) || 0,
    maxGb: IBM_VOL_LIMITS.max,
    imageName: (f && (f.imageMinSizeFrom || f.serverImageName)) || '',
});

// Empty is allowed - it means "let the deploy pick the image minimum".
const volumeSizeValid = (value, f) => {
    if (value === '' || value === null || value === undefined) return true;
    const num = parseInt(value, 10);
    if (isNaN(num)) return false;
    const { minGb } = imageConstraintOf(f);
    return num >= Math.max(IBM_VOL_LIMITS.min, minGb) && num <= IBM_VOL_LIMITS.max;
};

const IbmCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, formInvalidReason,
        availability, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({
        // IBM hides the OS picker (`hideOs` below) and derives the OS from the
        // selected image, so it must not be required before saving.
        provider: 'ibm', props, formHelpers, requiresOs: false,
        extraValidation: (f) => {
            if (!f.zoneId) return 'an availability zone';
            if (!volumeSizeValid(f.serverVolumeSize, f)) return 'a valid Server Volume size';
            if (!volumeSizeValid(f.nodeVolumeSize, f)) return 'a valid Node Volume size';
            return true;
        },
    });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, selectedZoneAvailable, selectedZoneMissing,
    } = availability;

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="ibm" providerLabel="IBM Cloud" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} formInvalidReason={formInvalidReason} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                        />
                    </DialogTitle>

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">

                                <BasicInformationSection
                                    form={form} setField={setField} provider="ibm"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                    hideImage hideOs
                                />

                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard
                                            icon="cloud" title="Infrastructure"
                                            action={form.zoneId ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={selectedZoneAvailable}
                                                    missingInstances={selectedZoneMissing} error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="ibm" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <RegionSelector provider="ibm" value={form.regionId || ''}
                                                    onChange={(id) => setField('regionId', id)} />
                                                <ZoneSelector provider="ibm" regionId={form.regionId || ''}
                                                    value={form.zoneId || ''} onChange={(id) => setField('zoneId', id)} />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <InstanceSizeSelector provider="ibm" value={form.serverInstanceId || ''}
                                                    onChange={(id) => setField('serverInstanceId', id)} label="Head Node Size" />
                                            </div>
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        {/* Image picker sits next to Infrastructure so the user fills
                                            region + credential first, then selects the image. */}
                                        <ImageSelectionSection
                                            form={form} setField={setField} provider="ibm"
                                            disableStackFields={marketplace.disableStackFields}
                                        />
                                    </div>
                                </div>

                                {advanced && (
                                    <ResourceManagerConfiguration form={form} setField={setField} lockManager={Boolean(marketplace.config?.resource_manager)} />
                                )}

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="ibm" advanced={advanced}
                                    marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        <div style={columnStyle}>
                                            <NetworkSettingsSection form={form} setField={setField} fillHeight />
                                        </div>
                                        <div style={columnStyle}>
                                            <SectionCard icon="speed" title="Fast Capacity Validation" fillHeight>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.capacityReservationParallelDeployment ?? false}
                                                            // Only the parallel flag: IBM has no capacity-reservation
                                                            // resource, so nothing reads enableCapacityReservation for
                                                            // this provider. Setting it too just implied it mattered.
                                                            onChange={(e) => setField('capacityReservationParallelDeployment', e.target.checked)}
                                                            name="capacityReservationParallelDeployment" color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                                                                Parallel Resources Provisioning
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary"
                                                                style={{ display: 'block', marginTop: '4px' }}>
                                                                Detects capacity issues faster and reduces total deployment time.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </SectionCard>
                                        </div>
                                    </div>
                                )}

                                {advanced && (
                                    <StorageSettingsSection
                                        form={form} setField={setField} provider="ibm"
                                        volSizeLimits={IBM_VOL_LIMITS}
                                        imageConstraint={imageConstraintOf(form)}
                                        volSizeNote={
                                            form.imageMinSizeGb
                                                ? `IBM boots each machine from a copy of the selected image, so the disk can never be smaller than the image itself — ${form.imageMinSizeFrom || form.serverImageName} needs at least ${form.imageMinSizeGb} GB. Boot disks max out at ${IBM_VOL_LIMITS.max} GB and are general-purpose volumes (3 IOPS per GB, so ~750 IOPS at ${IBM_VOL_LIMITS.max} GB): keep datasets and scratch on a Head Node Performance Data Storage volume (sdp profile, up to 64k IOPS) rather than the boot disk. Leave blank to use the image's size (marketplace images default to ${IBM_VOL_LIMITS.max} GB).`
                                                : `IBM boots each machine from a copy of the selected image, so the disk can never be smaller than the image itself. Pick a credential, region and image above and the exact minimum will appear here. Boot disks max out at ${IBM_VOL_LIMITS.max} GB and are general-purpose volumes (3 IOPS per GB): keep datasets and scratch on a Head Node Performance Data Storage volume (sdp profile, up to 64k IOPS) rather than the boot disk.`
                                        }
                                        shareOptions={[{ value: 'nfs', label: 'NFS' }]}
                                    />
                                )}
                                {advanced && (
                                    <ClusterStorageConfig
                                        value={form.storageConfiguration}
                                        onChange={(newConfig) => setField('storageConfiguration', newConfig)}
                                        disabled={disableInputs} cloudProvider="ibm"
                                    />
                                )}

                                <HeadNodePDSSection form={form} setField={setField} />

                                <ComputeNodesSection
                                    form={form} setField={setField} provider="ibm"
                                    disabled={disableInputs}
                                    marketplaceComputeIds={marketplace.allowedComputeIds}
                                />

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default IbmCluster;
